import React from 'react';
import { LightbulbIcon, SparkleIcon } from './Icons';

function renderInline(text) {
  return text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={i} className="inst-inline-code">{part.slice(1, -1)}</code>;
    }
    return part;
  });
}

function renderDescription(text) {
  if (!text) return null;
  return text.split(/(```[\s\S]*?```)/).map((seg, i) => {
    if (seg.startsWith('```') && seg.endsWith('```')) {
      const code = seg.slice(3, -3).replace(/^\n/, '');
      return <pre key={i} className="inst-code-block"><code>{code}</code></pre>;
    }
    return <span key={i}>{renderInline(seg)}</span>;
  });
}

function getSyllabusRef(id) {
  if (!id) return '';
  const match = id.match(/^(?:[Bb])?(\d+\.\d+(?:\.\d+)?)/);
  return match ? `B${match[1]}` : '';
}

const KIND_LABELS = {
  main:       'Main',
  scaffold:   'Scaffolded',
  stretch:    'Stretch',
  explore:    'Explore',
  trace:      'Trace',
  modify:     'Modify',
  create:     'Create',
  synthesise: 'Synthesise',
};

const KIND_CLASSES = {
  main:       'chip-kind-main',
  scaffold:   'chip-kind-scaffold',
  stretch:    'chip-kind-stretch',
  explore:    'chip-kind-explore',
  trace:      'chip-kind-trace',
  modify:     'chip-kind-modify',
  create:     'chip-kind-create',
  synthesise: 'chip-kind-synthesise',
};

export default function InstructionsPanel({
  exercise,
  feedback,
  consecutiveFails,
  unitComplete,
  unitTitle,
  allPassed,
  nextExercise,
  isRetry,
  nextReason,
  willOfferScaffold,
}) {
  if (!exercise) {
    return (
      <div className="card instructions-card">
        <div className="inst-loading">Loading exercise…</div>
      </div>
    );
  }

  const kind        = (exercise.type || 'main').toLowerCase();
  const syllabusRef = getSyllabusRef(exercise.id);

  return (
    <div className="card instructions-card">

      {/* ── Card header ───────────────────────────────────────── */}
      <div className="inst-header">
        <div className="inst-chips">
          {syllabusRef && (
            <span className="chip chip-accent">{syllabusRef}</span>
          )}
          {kind && kind !== 'main' && KIND_LABELS[kind] && (
            <span className={`chip ${KIND_CLASSES[kind] || 'chip-kind-main'}`}>
              {KIND_LABELS[kind]}
            </span>
          )}
        </div>
        <h1 className="inst-title">{exercise.title}</h1>
      </div>

      {/* ── Scrollable body ───────────────────────────────────── */}
      <div className="inst-body">

        {/* Description */}
        <div className="inst-description">{renderDescription(exercise.description)}</div>

        {/* Tinker prompt — explore exercises only */}
        {exercise.tinker_prompt && (
          <div className="tinker-prompt">
            <span className="tinker-icon" aria-hidden="true">🔬</span>
            <span>{renderDescription(exercise.tinker_prompt)}</span>
          </div>
        )}

        {/* Hints — shown only after a submission returns hints */}
        {feedback?.hints?.length > 0 && (
          <div className="hints-section">
            <div className="hints-heading">
              <SparkleIcon size={11} />
              <span>HINTS</span>
            </div>
            {feedback.hints.map((hint, i) => (
              <details key={i} className="hint-item">
                <summary className="hint-summary">
                  <span className="hint-badge">{i + 1}</span>
                  Reveal hint {i + 1}
                </summary>
                <div className="hint-body">
                  <LightbulbIcon size={13} />
                  <span>{hint}</span>
                </div>
              </details>
            ))}
          </div>
        )}

        {/* Feedback block — pass or fail message */}
        {feedback && (
          <div className={`feedback-block ${feedback.passed ? 'fb-pass' : 'fb-fail'}`}>
            <p className="fb-message">{feedback.message}</p>
            {willOfferScaffold && (
              <p className="fb-note">
                A scaffolded version will be offered on your next attempt.
              </p>
            )}
            {isRetry && nextReason && (
              <p className="fb-note">{nextReason}</p>
            )}
          </div>
        )}

      </div>

      {/* ── Sticky footer ─────────────────────────────────────── */}
      <div className="inst-footer">
        {unitComplete ? (
          <div className="unit-complete-banner">
            🎉 You've completed {unitTitle}!
          </div>
        ) : nextExercise && !isRetry && (allPassed || willOfferScaffold) ? (
          <>
            {nextReason && <p className="next-reason">{nextReason}</p>}
            <p className="continue-hint">
              {willOfferScaffold && !allPassed
                ? 'Use the → arrow above to try the simpler version'
                : 'Use the → arrow above to continue'}
            </p>
          </>
        ) : null}
      </div>

    </div>
  );
}
